import {
    NinchatProperties,
    NinchatStore,
} from 'api/types'
import {getActiveQueueRegisteredTicketCount} from 'utils/queueSettings'
import {getGroupQueues} from 'selectors/ninchat/groups'

type GroupQueue = ReturnType<typeof getGroupQueues>[number]

type TicketDurationLoadOptions = {
    groupQueues: GroupQueue[]
    parentGroupQueue?: NinchatProperties.NinchatUserQueue
    queue: NinchatProperties.NinchatUserQueue | undefined
    queueId: NinchatProperties.QueueID
}

type TicketDurationCommentRequestOptions = {
    audienceComments: NinchatStore.NinchatUserAudienceCommentsStore
    audienceTranscripts: NinchatStore.NinchatAudienceTranscriptsStore
    queueActivities: NinchatStore.NinchatUserQueuesActivityStore
    queueIds: NinchatProperties.QueueID[]
}

type TicketDurationCommentRequest = {
    audience_first: NinchatProperties.AudienceID
    audience_last: NinchatProperties.AudienceID
    queue_id: NinchatProperties.QueueID
}

export const hasLoadedComments = (
    comments: NinchatProperties.NinchatAudienceRangeComments | undefined,
    audienceId: NinchatProperties.AudienceID,
): boolean => !!comments && comments?.[audienceId] !== undefined

const getAudienceRange = (
    activities: NinchatProperties.NinchatQueueActivity[],
    options: {
        audienceComments: NinchatProperties.NinchatAudienceRangeComments | undefined
        audienceTranscripts: NinchatStore.NinchatAudienceTranscriptsStore
    },
): Pick<TicketDurationCommentRequest, 'audience_first' | 'audience_last'> | undefined => {
    const audienceIds = activities
        .filter((activity) => activity.event === 'audience_complete' && activity.audience_id)
        .map((activity) => activity.audience_id as NinchatProperties.AudienceID)
        .filter((audienceId) => !options.audienceTranscripts[audienceId] && !hasLoadedComments(options.audienceComments, audienceId))

    if (audienceIds.length === 0) {
        return undefined
    }

    return audienceIds.reduce<Pick<TicketDurationCommentRequest, 'audience_first' | 'audience_last'>>((range, audienceId) => ({
        audience_first: audienceId < range.audience_first ? audienceId : range.audience_first,
        audience_last: audienceId > range.audience_last ? audienceId : range.audience_last,
    }), {
        audience_first: audienceIds[0],
        audience_last: audienceIds[0],
    })
}

export const getQueueIdsForTicketing = ({
    groupQueues,
    parentGroupQueue,
    queue,
    queueId,
}: TicketDurationLoadOptions): NinchatProperties.QueueID[] => {
    if (!queue) {
        return []
    }

    if (!queue.queue_attrs.is_group) {
        const registeredTicketCount = getActiveQueueRegisteredTicketCount({
            parentGroupQueue,
            queue,
        })

        return registeredTicketCount > 0 ? [queueId] : []
    }

    return groupQueues
        .filter((groupQueue) => getActiveQueueRegisteredTicketCount({
            parentGroupQueue: queue,
            queue: groupQueue.queueProps,
        }) > 0)
        .map((groupQueue) => groupQueue.id)
}

export const getTicketDurationCommentRequests = ({
    audienceComments,
    audienceTranscripts,
    queueActivities,
    queueIds,
}: TicketDurationCommentRequestOptions): TicketDurationCommentRequest[] => queueIds.flatMap((queue_id) => {
    const activities = queueActivities?.[queue_id]
    if (activities === undefined) {
        return []
    }

    const range = getAudienceRange(activities, {
        audienceComments: audienceComments[queue_id],
        audienceTranscripts,
    })

    return range
        ? [{
            ...range,
            queue_id,
        }]
        : []
})
